import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { ShoppingLists,  ShoppingListEntry, ShoppingList, Product, FileProduct, BasketProduct, ShoppingListLine } from '../_models';
import { map,  catchError, tap, switchMap, flatMap, mergeMap, concatMap } from 'rxjs/operators';
import { Observable, BehaviorSubject, throwError, of, forkJoin } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { CatalogService } from './catalog.service';
import { AuthenticationService } from './authentication.service';

@Injectable({ providedIn: 'root' })
export class ListsService {
   
    private id_user : string;
    public userLists : Observable<ShoppingListEntry[]>;
    public userListsSubject : BehaviorSubject<ShoppingListEntry[]>;
    public hasLists : boolean;

    constructor(
        private http: HttpClient,
        private toastr : ToastrService,
        private translate : TranslateService,
        private catalog : CatalogService,
        private auth : AuthenticationService
     ) {

        this.userListsSubject = new BehaviorSubject<ShoppingListEntry[]>(null);
        this.userLists = this.userListsSubject.asObservable();
        this.auth.currentUser.subscribe(user => {
            this.id_user = null;
            if (user) {
                this.id_user = user.id_user;
                this.getShopping().subscribe();
            } 
        });
        
    }

    /*
    private getAll() : Observable<AbstractListEntry[]> {
        return this.http.get<AbstractLists>(`${environment.API_ENDPOINT}abstractlists/${this.id_user}/ls`).pipe(
            map (result => {               
                this.userListsSubject.next(result.listentries.filter( list => {
                    return list.entrysubclass == 'LlistaCompra_cap';
                }));
                return this.userListsSubject.value;
            }),
            catchError (error => {
                this.toastr.error(this.translate.instant('USERLISTS.ERROR_GET'));
                this.userListsSubject.next(null);
                throw error;
            })
        );
    }
    */

   private getShopping() {
        return this.http.get<ShoppingLists>(`${environment.API_ENDPOINT}shoplists/${this.id_user}/ls`).pipe(
            map (result => {
                this.userListsSubject.next(result.listentries);
                this.hasLists = result.listentries.length > 0;
                return this.userListsSubject.value;
            }),
            catchError (error => {
                this.toastr.error(this.translate.instant('USERLISTS.ERROR_GET'));
                throw error;
            })
        );
    }

    public add(name : string) {
        // https://tweb3.plusfresc.cat/api/shoplists/{user}/edit
        let  data = {
            "custid" : "",
            "listid" : 0,
            "listname" : name,
            "copyfromlistid" : 0,
            "focused" : true
        }
        
        return this.http.post<number>(`${environment.API_ENDPOINT}shoplists/${this.id_user}/edit`,data).pipe(
            map ( result  => {
                this.getShopping().subscribe();
                this.toastr.success(this.translate.instant('USERLISTS.ADDED'));
                return result;
            }),
            catchError (error => {
                this.toastr.error(this.translate.instant('USERLISTS.ERROR_ADD'));
                throw error;
            })
        );
    }
    
    public edit(listId: number , name : string) {
        // https://tweb3.plusfresc.cat/api/shoplists/{user}/edit
        let  data = {
            "custid" : "",
            "listid" : listId,
            "listname" : name,
            "copyfromlistid" : 0           
        }
        
        return this.http.post<number>(`${environment.API_ENDPOINT}shoplists/${this.id_user}/edit`,data).pipe(
            map ( result  => {
                this.getShopping().subscribe();
                this.toastr.success(this.translate.instant('USERLISTS.EDITED'));
                return result;
            }),
            catchError (error => {
                this.toastr.error(this.translate.instant('USERLISTS.ERROR_EDIT'));
                throw error;
            })
        );
    }

    public addFromBasket(basketId: number, name : string) {
        return this.http.put<number>(`${environment.API_ENDPOINT}shoplists/${this.id_user}/${basketId}/${name}/createfrombasket`,null).pipe(
            map ( result  => {
                this.getShopping().subscribe();
                this.toastr.success(this.translate.instant('USERLISTS.ADDED_FROM_BASKET'));
                return result;
            }),
            catchError (error => {
                this.toastr.error(this.translate.instant('USERLISTS.ERROR_ADD_FROM_BASKET'));
                throw error;
            })
        );

    }

    public delete (listid : number){
        // https://tweb3.plusfresc.cat/api/shoplists/pepito/del/18     
        return this.http.delete(`${environment.API_ENDPOINT}shoplists/${this.id_user}/del/${listid}`).pipe(
            flatMap( result =>{
                this.toastr.success(this.translate.instant('USERLISTS.DELETED'));
                return this.getShopping();
            }),
            catchError (error => {
                this.toastr.error(this.translate.instant('USERLISTS.ERROR_DELETE'));
                throw error;
            })
        );     
      }

    public get (listid : number, lang : string) : Observable<ShoppingList>{
        // https://tweb3.plusfresc.cat/api/shoplist/{user}/{listid}/{lang}/view
        return this.http.get<ShoppingList>(`${environment.API_ENDPOINT}shoplist/${this.id_user}/${listid}/${lang}/view`).pipe(
            map( result => {
                let list = new ShoppingList(result);
                return list;               
            }),
            catchError (error => {
                this.toastr.error(this.translate.instant('USERLISTS.ERROR_GET_LIST'));
                throw error;
            })
        );

    }

    public addLine (listid : number, lineid : number , product : Product, quantity : number ){
        
        // if no list , get focused list
        if (listid == 0){
            const focusedList : ShoppingListEntry = this.userListsSubject.value.find( list => { 
                return list.focused 
            });
            if (focusedList) {
                listid = focusedList.id;
            }
        }

        let  data = {
            "custid" : this.id_user,
            "listid" : listid,
            "lineid" : lineid,
            "nodeid" :  product.id,
            "quantity" : quantity,
            "formatservice" : "",
            "notes" : ""
        }
       
        return this.http.post<any>(`${environment.API_ENDPOINT}shoplist/${this.id_user}/item`,data).pipe(
            map (result => {
                this.getShopping().subscribe();
                this.toastr.success(this.translate.instant('USERLISTS.ADDED_LINE'));
                return result.lineid;
            }),
            catchError (error => {
                this.toastr.error(this.translate.instant('USERLISTS.ERROR_ADD_LINE'));
                throw error;
            })
        );

    }

    public addFormatLine (listid : number, lineid : number , product : BasketProduct ){
        
        // if no list , get focused list
        if (listid == 0){
            const focusedList : ShoppingListEntry = this.userListsSubject.value.find( list => { 
                return list.focused 
            });
            if (focusedList) {
                listid = focusedList.id;
            }
        }

        let  data = {
            "custid" : this.id_user,
            "listid" : listid,
            "lineid" : lineid,
            "nodeid" :  product.product.id,
            "quantity" : product.quantity,
            "formatservice" : product.selected_format,
            "notes" : product.notes
        }

       
        return this.http.post<any>(`${environment.API_ENDPOINT}shoplist/${this.id_user}/item`,data).pipe(
            map (result => {
                this.getShopping().subscribe();
                this.toastr.success(this.translate.instant('USERLISTS.ADDED_LINE'));
                return result.lineid;
            }),
            catchError (error => {
                this.toastr.error(this.translate.instant('USERLISTS.ERROR_ADD_LINE'));
                throw error;
            })
        );
    }

    public deleteLine (listid : number, lineid : number ){
        // https://tweb3.plusfresc.cat/api/shoplist/pepito/17/2/del
        return this.http.delete(`${environment.API_ENDPOINT}shoplist/${this.id_user}/${listid}/${lineid}/del`).pipe(
            tap (result =>{
                this.toastr.success(this.translate.instant('USERLISTS.REMOVED_LINE'));
            }),
            catchError (error => {
                this.toastr.error(this.translate.instant('USERLISTS.ERROR_DELETE_LINE'));
                throw error;
            })
        );
    }
}
